import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import * as NavigationActions from "../../../actions/navigation";
import * as ProjectActions from "../../../actions/project";
import * as ProjectInvestigatorActions from "../../../actions/projectInvestigator";
import * as TrialActions from "../../../actions/trial";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    DatePickerFormInputFabric,
    TextAreaFormInput,
} from "../../../components/form";
import { SelectFormInput, TextFormInput } from "../../../components/form/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    ProjectCreateBreadcrumbs,
    ProjectDetailsBreadcrumbs,
    TrialProjectDetailsBreadcrumbs,
} from "../../../helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { ProjectSubMenu, TrialProjectSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ProjectLayout } from "../../index";
import ProjectConfirmationModal from "./ProjectConvertConfirmationModal";

import "./ProjectPage.scss";

interface ProjectPageProps {
    form: Dtos.Project & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    projectId: number;
    project: Dtos.Project;
    lookups: Dtos.Lookup[];
    trial: Dtos.Trial;
    loadingTrial: boolean;
    loadingProject: boolean;
    loadProjectSuccess: boolean;
    loadProjectFailure: boolean;
    loadProjectErrorCode: string;
    savingProject: boolean;
    saveProjectSuccess: boolean;
    saveProjectFailure: boolean;
    creatingProject: boolean;
    createProjectSuccess: boolean;
    validatingProject: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    projectInvestigators: Dtos.ProjectInvestigator[];
    loadingProjectInvestigators: boolean;
    loadProjectInvestigatorsSuccess: boolean;
    loadProjectInvestigatorsFailure: boolean;
    loadProjectInvestigatorsErrorCode: string;
    clearProject: ProjectActions.IProjectClearActionCreator;
    loadProject: ProjectActions.IProjectLoadByIdActionCreator;
    saveProject: ProjectActions.IProjectSaveActionCreator;
    createProject: ProjectActions.IProjectCreateActionCreator;
    validateProject: ProjectActions.IProjectValidateActionCreator;
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    clearProjectInvestigators: ProjectInvestigatorActions.IProjectInvestigatorClearActionCreator;
    loadProjectInvestigators: ProjectInvestigatorActions.IProjectInvestigatorsLoadByProjectIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
}

const reduxFormName: string = "reduxForms.project";

const ProjectPage: FC<ProjectPageProps> = ({
    form,
    formState,
    formProperties,
    project,
    lookups,
    trial,
    loadingTrial,
    loadingProject,
    loadProjectFailure,
    loadProjectErrorCode,
    savingProject,
    saveProjectSuccess,
    saveProjectFailure,
    creatingProject,
    createProjectSuccess,
    validatingProject,
    validationFailures,
    permissions,
    projectInvestigators,
    loadingProjectInvestigators,
    //loadProjectInvestigatorsSuccess,
    loadProjectInvestigatorsFailure,
    loadProjectInvestigatorsErrorCode,
    clearProject,
    loadProject,
    saveProject,
    createProject,
    validateProject,
    clearTrial,
    loadTrial,
    loadForm,
    changeForm,
    resetForm,
    clearProjectInvestigators,
    loadProjectInvestigators,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { projectId } = useParams<{ projectId: string }>();
    const projectIdNumber = parseInt(projectId, 10);

    //
    // -- Hooks --
    const location = useLocation();
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Project = {} as Dtos.Project;

        loadForm(reduxFormName, Object.assign({ ...project }, extraData));
    };


    // Load project/trial
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            projectIdNumber &&
            baseRoute === "trials" &&
            !trial &&
            !loadingTrial
        ) {
            setDrawerItems(TrialProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(TrialProjectDetailsBreadcrumbs(projectIdNumber));

            loadProject(projectIdNumber);
            loadTrial(projectIdNumber);
            loadProjectInvestigators(projectIdNumber);
        } else if (
            projectIdNumber &&
            baseRoute === "projects" &&
            !project &&
            !loadingProject &&
            !loadProjectFailure
        ) {
            setDrawerItems(ProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(ProjectDetailsBreadcrumbs(projectIdNumber));
            loadProject(projectIdNumber);
            loadProjectInvestigators(projectIdNumber);
        } else if (
            !projectIdNumber &&
            !creatingProject &&
            !createProjectSuccess
        ) {
            setBreadcrumbs(ProjectCreateBreadcrumbs());
            setDrawerItems([]);
            createProject();
        } else if (project && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        projectIdNumber,
        trial,
        project,
        permissions,
        setDrawerItems,
        TrialProjectSubMenu,
        setBreadcrumbs,
        TrialProjectDetailsBreadcrumbs,
        loadTrial,
        ProjectSubMenu,
        ProjectDetailsBreadcrumbs,
        createProject,
        ProjectCreateBreadcrumbs,
        loadReduxForm,
        initalised,
        setInitalised,
        loadingTrial,
        loadingProject,
        location,
        loadProject,
        creatingProject,
        createProjectSuccess,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProjectFailure) {
            navigate(`/error/${loadProjectErrorCode}`);
        }
        if (loadProjectInvestigatorsFailure) {
            navigate(`/error/${loadProjectInvestigatorsErrorCode}`);
        }
    }, [
        loadProjectFailure, 
        loadProjectErrorCode, 
        loadProjectInvestigatorsFailure,
        loadProjectInvestigatorsErrorCode,
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            projectIdNumber &&
            baseRoute === "trials" &&
            trial?.displayName &&
            trial?.projectProtocolID
        ) {
            setDrawerItems(TrialProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(
                TrialProjectDetailsBreadcrumbs(
                    projectIdNumber,
                    trial.displayName,
                    trial.projectProtocolID
                )
            );
        } else if (
            projectIdNumber &&
            baseRoute === "projects" &&
            project?.projectShortTitle
        ) {
            setDrawerItems(ProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(
                ProjectDetailsBreadcrumbs(
                    projectIdNumber,
                    project.projectShortTitle
                )
            );
        }
    }, [
        projectIdNumber,
        project,
        permissions,
        trial,
        location,
        setDrawerItems,
        setBreadcrumbs,
        ProjectSubMenu,
        ProjectDetailsBreadcrumbs,
        TrialProjectSubMenu,
        TrialProjectDetailsBreadcrumbs,
    ]);

    // Reload project/trial after saving
    useEffect(() => {
        if (!savingProject && saveProjectSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Project saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            const baseRoute = location?.pathname?.split("/")[1];
            if (saveAndReturn) {
                let url: string =
                    baseRoute === "trials" ? "/trials" : `/projects`;
                navigate(url);
            } else if (!projectIdNumber) {
                let url: string =
                    baseRoute === "trials"
                        ? `/trials/${trial.id}/details`
                        : `/projects/${project.id}/details`;
                navigate(url);
                clearProject();
            } else if (project.projectOutcomeId == 2) {
                navigate(`/trials/${project.id}/details`);
            }
        }
    }, [
        savingProject,
        saveProjectSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingProject && saveProjectFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingProject, saveProjectSuccess]);

    // Cleanup effect to clear the project on component unmount
    useEffect(() => {
        return () => {
            clearProject();
            clearTrial();
            clearProjectInvestigators();
            resetForm(reduxFormName);
        };
    }, [clearProject, clearTrial, clearProjectInvestigators, resetForm, reduxFormName]);

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];
        let url: string =
            baseRoute === "trials"
                ? `/trials/${trial.id}/details`
                : `/projects/${project.id}/details`;
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.Project) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Project = {} as Dtos.Project;

            await saveProject(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Project = {} as Dtos.Project;

            if (form && hasBeenSubmitted && !validatingProject) {
                await validateProject(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const baseRoute = location?.pathname?.split("/")[1];
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionForId(
                projectIdNumber,
                permissions,
                Dtos.Permission.ProjectUpdate,
                Dtos.Permission.ProjectCreate
            );
        const hasProjectInvestigatorPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionForId(
                projectIdNumber,
                permissions,
                Dtos.Permission.ProjectInvestigatorsUpdate,
                Dtos.Permission.ProjectInvestigatorsCreate
            );

        const canViewProjectInvestigators = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.ProjectInvestigatorsView,
            null,
            null
        );
        const formDisabled: boolean =
            !hasPermission ||
            savingProject ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled) ||
            baseRoute === "trials";
        const canCreateProjectInvestigator: boolean =
            hasProjectInvestigatorPermission &&
            projectIdNumber > 0 &&
            baseRoute === "projects";


        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "project",
                                        {
                                            projectProtocolID: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "project",
                                        {
                                            projectScientificTitle: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "project",
                                        {
                                            projectShortTitle: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "project",
                                        {
                                            projectAckSource: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "project",
                                        {
                                            projectTrialDeveloperId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Project",
                                                                "ProjectTrialDeveloperId",
                                                                lookups
                                                            ),
                                                            form.projectTrialDeveloperId
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "project",
                                        {
                                            projectDateSubmitted: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-4",
                                                fieldClassName: "col-sm-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "project",
                                        {
                                            projectOutcomeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Project",
                                                                "ProjectOutcomeId",
                                                                lookups
                                                            ),
                                                            form.projectOutcomeId
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-3",
                                                fieldClassName: "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "project",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            investigatorsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div>
                        {canViewProjectInvestigators && projectIdNumber > 0 && (
                            <>
                                <br />
                                <h1>Investigators</h1>
                                <div className="card card-shadow p-3">
                                    {!projectInvestigators ||
                                    projectInvestigators.length == 0 ? (
                                        <>
                                            <div className="alert alert-info d-flex mb-0">
                                                <div className="mr-2">
                                                    <FontAwesomeIcon
                                                        icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                                        fixedWidth
                                                    />
                                                </div>
                                                <span>
                                                    There are currently no investigators to view.
                                                </span>
                                            </div>
                                            {canCreateProjectInvestigator && (
                                                <div className="mt-3 d-flex justify-content-end">
                                                    <Link
                                                        className={"btn btn-secondary btn-sm"}
                                                        url={`/projects/${projectIdNumber}/investigators/create`}
                                                    >
                                                        Add Investigator
                                                    </Link>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="table-wrapper">
                                            <ReactTable
                                                data={projectInvestigators}
                                                pageSizeOptions={PAGE_SIZE_OPTIONS}
                                                defaultPageSize={projectInvestigators.length}
                                                pageSize={projectInvestigators.length}
                                                showPagination={false}
                                                className="-striped -highlight -clickable"
                                                noDataText="There are currently no investigators to display"
                                                resizable={false}
                                                onPageSizeChange={(_val) => {}}
                                                getTdProps={(
                                                    _state,
                                                    rowInfo: RowInfo,
                                                    _column,
                                                    _instance
                                                ) => {
                                                    return {
                                                        onClick: (e, handleOriginal) => {
                                                            const baseRoute = location?.pathname?.split("/")[1];
                                                            const url: string =
                                                                baseRoute ===
                                                                    "trials"
                                                                        ? `/trials/${projectIdNumber}/project/investigators/${rowInfo.original.id}/details`
                                                                        : `/projects/${projectIdNumber}/investigators/${rowInfo.original.id}/details`;
                                                            navigate(url);

                                                            if (handleOriginal) {
                                                                handleOriginal();
                                                            }
                                                        },
                                                        style: {
                                                            color:
                                                                rowInfo &&
                                                                rowInfo.original &&
                                                                rowInfo.original
                                                                    .active
                                                                    ? "#212529"
                                                                    : "#9e9e9e",
                                                        },
                                                    };
                                                }}
                                                defaultSorted={[
                                                    {
                                                        id: "personnel.displayWithSalutation",
                                                        desc: false,
                                                    },
                                                ]}
                                                columns={[
                                                    {
                                                        id: "id",
                                                        Header: " ",
                                                        accessor: "id",
                                                        maxWidth: 50,
                                                        Cell: (_props) => (
                                                            <FontAwesomeIcon
                                                                size="lg"
                                                                icon={FontAwesomeIcons.Duotone.USER}
                                                                fixedWidth
                                                            />
                                                        ),
                                                    },
                                                    {
                                                        id: "displayWithSalutation",
                                                        Header: "Personnel",
                                                        accessor: "personnel.displayWithSalutation",
                                                    },
                                                    {
                                                        id: "companyName",
                                                        Header: "Organisation",
                                                        accessor: "personnel",
                                                        Cell: (props) => (
                                                            <span>{props.value?.personnelAddresses?.find(x => x.addressTypeId == 2 && x.companyName)?.companyName}</span>
                                                        ),
                                                    },
                                                ]}
                                            />
                                            {canCreateProjectInvestigator && (
                                                <div className="mt-3 d-flex justify-content-end">
                                                    <Link
                                                        className={"btn btn-secondary btn-sm"}
                                                        url={`/projects/${projectId}/investigators/create`}
                                                    >
                                                        Add Investigator
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                ),
            },
        };

        return (
            <>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Details</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <button
                            disabled={form?.projectOutcomeId == 2}
                            onClick={() => {
                                setIsOpen(true);
                            }}
                            type="button"
                            className="btn btn-primary"
                            style={{
                                cursor:
                                    form?.projectOutcomeId == 2
                                        ? "not-allowed"
                                        : "pointer",
                            }}
                        >
                            Convert to Trial
                        </button>
                    </div>
                </div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "project",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={() => onCancel()}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </>
        );
    }, [
        projectIdNumber,
        permissions,
        savingProject,
        formState,
        form,
        lookups,
        navigate,
        PAGE_SIZE_OPTIONS,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        validationFailures,
        formProperties,
        changeForm,
        location,
        project,
        setIsOpen,
        projectInvestigators
    ]);

    return (
        <>
            <ProjectLayout
                loading={
                    loadingProject || 
                    creatingProject || 
                    loadingTrial ||
                    loadingProjectInvestigators
                }
                permission={Dtos.Permission.ProjectView}
            >
                {renderContent}
            </ProjectLayout>
            <ProjectConfirmationModal
                project={project}
                saveProject={saveProject}
                validateProject={validateProject}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
            />
        </>
    );
};

const mapStateToProps = (state: ITmdState): Partial<ProjectPageProps> => {
    let project: Dtos.Project | undefined = !(
        state.projects.formData instanceof Array
    )
        ? state.projects.formData
        : undefined;
    let projectInvestigators: Dtos.ProjectInvestigator[] | undefined = (
        state.projectInvestigator.formData instanceof Array
    )
        ? state.projectInvestigator.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;

    return {
        project: project!,
        trial: trial!,
        projectInvestigators: projectInvestigators!,
        loadingTrial:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Pending,
        loadingProject:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Pending,
        loadProjectFailure:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Failure,
        loadProjectErrorCode: state.projects.loadState.errorCode,
        savingProject:
            state.projects.saveState &&
            state.projects.saveState.status == RequestState.Pending,
        saveProjectSuccess:
            state.projects.saveState &&
            state.projects.saveState.status == RequestState.Success,
        saveProjectFailure:
            state.projects.saveState &&
            state.projects.saveState.status == RequestState.Failure,
        creatingProject:
            state.projects.createState &&
            state.projects.createState.status == RequestState.Pending,
        createProjectSuccess:
            state.projects.createState &&
            state.projects.createState.status == RequestState.Success,
        validatingProject:
            state.projects.validateState &&
            state.projects.validateState.status == RequestState.Pending,
        loadingProjectInvestigators:
            state.projectInvestigator.loadState &&
            state.projectInvestigator.loadState.status == RequestState.Pending,
        loadProjectInvestigatorsSuccess:
            state.projectInvestigator.loadState &&
            state.projectInvestigator.loadState.status == RequestState.Success,
        loadProjectInvestigatorsFailure:
            state.projectInvestigator.loadState &&
            state.projectInvestigator.loadState.status == RequestState.Failure,
        loadProjectInvestigatorsErrorCode: state.projectInvestigator.loadState.errorCode,
        permissions: permissions,
        lookups: state.projects.lookups,
        form: state.reduxForms.project,
        formState: state.projects.formState,
        formProperties: state.projects.formProperties,
        validationFailures: project
            ? state.projects.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<ProjectPageProps> => {
    return {
        loadProject: bindActionCreators(
            ProjectActions.LoadProjectById,
            dispatch
        ),
        clearProject: bindActionCreators(ProjectActions.Clear, dispatch),
        saveProject: bindActionCreators(ProjectActions.SaveProject, dispatch),
        createProject: bindActionCreators(
            ProjectActions.CreateProject,
            dispatch
        ),
        validateProject: bindActionCreators(
            ProjectActions.ValidateProject,
            dispatch
        ),
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
        loadProjectInvestigators: bindActionCreators(ProjectInvestigatorActions.LoadProjectInvestigatorsByProjectId, dispatch),
        clearProjectInvestigators: bindActionCreators(ProjectInvestigatorActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectPage);
